/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ExternalLink,
  Trash2Icon,
} from 'lucide-react';
import { useState } from 'react';

/**
 * Internal dependencies
 */
import Breadcrumbs from '@Components/Breadcrumbs';
import { CopyableText } from '@Components/CopyableText';
import NotFound from '@Components/NotFound';
import PhonePreviewContainer from '@Components/PhonePreviewContainer';
import RichText from '@Components/RichText';
import ShowFor from '@Components/ShowFor';
import ArchiveEventDialog from '@Components/TicketedEvents/ArchiveEvent/ArchiveEventDialog';
import {
  EventArchivedBadge,
  EventBadges,
} from '@Components/TicketedEvents/Badges';
import EditTicketTypesSheet from '@Components/TicketedEvents/EditTicketTypesSheet';
import EditTicketedEventDetailsSheet from '@Components/TicketedEvents/EditTicketedEventDetailsSheet';
import EventPreview from '@Components/TicketedEvents/EventPreview';
import EventsSitePreview from '@Components/TicketedEvents/EventsSitePreview';
import RestoreEventDialog from '@Components/TicketedEvents/RestoreEvent/RestoreEventDialog';
import TicketOrders from '@Components/TicketedEvents/TicketOrders';
import { useAuth } from '@Contexts/auth';
import { cn } from '@Utils/cn';
import { formatCurrency, formatZonedDateTime } from '@Utils/utils';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import FeaturedImageDialog from '../../../components/FeaturedImageDialog';
import ManageGalleryImagesDialog from '../../../components/ManageGalleryImagesDialog';
import { useTicketedEvent } from '../../../hooks/useTicketedEvent';
import { useUpdateTicketedEvent } from '../../../hooks/useUpdateTicketedEvent';
import { useVenue } from '../../../hooks/useVenue';
import { useEventPreviewStore } from '../../../stores/eventPreviewStore';

export default function TicketedEventDetails() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const {
    data: event,
    status,
    isError,
  } = useTicketedEvent({
    eventId,
    include:
      'venue,featuredImage,images,ticketTypes,categories,tags,organizers',
  });

  const { data: venue } = useVenue({
    venueId: event?.venueId,
    toastInclude: 'toastRestaurant',
    enabled: !!event?.venueId,
  });

  const [eventDetailsSheetOpen, setEventDetailsSheetOpen] = useState(false);
  const [eventArchiveSheetOpen, setEventArchiveSheetOpen] = useState(false);
  const [eventRestoreDialogOpen, setEventRestoreDialogOpen] = useState(false);
  const [ticketTypesSheetOpen, setTicketTypesSheetOpen] = useState(false);
  const [featuredImageDialogOpen, setFeaturedImageDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'members-app' | 'events-site'>(
    'members-app',
  );

  const queryClient = useQueryClient();
  const updateEventMutation = useUpdateTicketedEvent({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticketedEvent'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['ticketed-events'] });
    },
  });

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !event) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Ticketed Events', href: '/ticketed-events' },
          { name: event.name, href: `/ticketed-events/${eventId}` },
        ]}
      />

      <div className="mt-8 mb-4">
        <Heading>{event.name}</Heading>
        <p className="mb-1 text-sm font-bold text-zinc-500">
          EVENT AT {venue?.name ? venue?.name.toUpperCase() : ''}
        </p>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="ticket-types">Ticket Types</TabsTrigger>
          <TabsTrigger value="ticket-orders">Ticket Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="mt-12">
          <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
            <div className="mb-12 flex flex-col space-y-8 xl:col-span-2">
              {/* Ticket Orders */}
              <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
                <div className="col-span-3">
                  <div className="mb-2 flex items-center justify-between">
                    <Heading level="h3">Details</Heading>
                    <div className="flex items-center space-x-2">
                      <ShowFor permissions={['EVENTS_WRITE']}>
                        {event.archivedAt ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEventRestoreDialogOpen(true)}
                            icon={{
                              src: <ArchiveRestoreIcon className="h-4 w-4" />,
                            }}
                          >
                            Restore
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEventArchiveSheetOpen(true)}
                            icon={{
                              src: <ArchiveIcon className="h-4 w-4" />,
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            Archive
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEventDetailsSheetOpen(true)}
                        >
                          Edit
                        </Button>
                      </ShowFor>
                    </div>
                  </div>
                  <Card>
                    <CardContent>
                      <div className="relative">
                        <div className="flex flex-col space-y-2.5">
                          <div>
                            <div className="text-sm font-bold text-zinc-500">
                              Name
                            </div>
                            {event.name}
                            <div className="mt-1 flex flex-wrap gap-1">
                              <EventBadges event={event} />
                              <EventArchivedBadge event={event} />
                            </div>
                          </div>
                          {event.teaser && (
                            <div>
                              <div className="text-sm font-bold text-zinc-500">
                                Teaser
                              </div>
                              {event.teaser}
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4 md:grid">
                            <div>
                              <div className="text-sm font-bold text-zinc-500">
                                Start Date & Time
                              </div>
                              {formatZonedDateTime(
                                event.startDateTime,
                                "MMMM dd, yyyy 'at' hh:mm a",
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-zinc-500">
                                End Date & Time
                              </div>
                              {formatZonedDateTime(
                                event.endDateTime,
                                "MMMM dd, yyyy 'at' hh:mm a",
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-bold text-zinc-500">
                              Capacity
                            </div>
                            {event.capacity}
                          </div>
                          {event.slug && (
                            <div>
                              <div className="text-sm font-bold text-zinc-500">
                                Slug
                              </div>
                              <CopyableText
                                text={event.slug}
                                tooltipContent="Copy slug"
                                copyValue={event.slug}
                              />
                            </div>
                          )}
                          <div>
                            <div className="mb-2 text-sm font-bold text-zinc-500">
                              Description
                            </div>
                            <div className="rounded-xl bg-zinc-100 p-4">
                              <ScrollArea className="h-96 overflow-auto">
                                <RichText
                                  content={event.description}
                                  className="max-w-full!"
                                />
                              </ScrollArea>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="col-span-2 space-y-8">
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <Heading level="h3">Location</Heading>
                    </div>
                    <Card>
                      <CardContent>
                        <div className="flex flex-col space-y-2.5">
                          <div>
                            <div className="text-sm font-bold text-zinc-500">
                              Venue
                            </div>
                            {event.venue && event.venueId ? (
                              <Link to={`/venues/${event.venue.id}`}>
                                {event.venue.name}
                              </Link>
                            ) : (
                              <span className="text-zinc-500">
                                No venue assigned
                              </span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="flex flex-col space-y-8">
                    <div className="mb-2">
                      <Heading level="h3">Photos</Heading>
                    </div>
                    <div>
                      <Card>
                        <CardContent>
                          <div className="space-y-4">
                            {/* Featured Image */}
                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <div className="text-sm font-medium">
                                  Featured Image
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setFeaturedImageDialogOpen(true)
                                  }
                                >
                                  Edit featured image
                                </Button>
                              </div>
                              {event.featuredImage ? (
                                <div className="relative flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                                  <CloudinaryImage
                                    publicId={event.featuredImage.cloudinaryId}
                                    mode="original"
                                    className="h-64 w-full object-contain"
                                  />
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="absolute top-2 right-2"
                                    disabled={updateEventMutation.isPending}
                                    onClick={() =>
                                      updateEventMutation.mutate(
                                        {
                                          eventId: event.id,
                                          payload: {
                                            featuredImageId: null,
                                          },
                                        },
                                        {
                                          onSuccess: () =>
                                            toastSuccess(
                                              'Featured image removed.',
                                            ),
                                        },
                                      )
                                    }
                                  >
                                    <Trash2Icon className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                                  <div className="text-sm text-zinc-500">
                                    No featured image set
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Gallery Images */}
                            <div>
                              <div className="mb-2 flex items-center justify-between">
                                <div className="text-sm font-medium">
                                  Gallery
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setGalleryDialogOpen(true)}
                                >
                                  Manage images
                                </Button>
                              </div>
                              {event.images && event.images.length > 0 ? (
                                <div className="grid grid-cols-2 gap-2">
                                  {event.images.map((image) => (
                                    <div
                                      key={image.id}
                                      className="flex min-h-32 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100"
                                    >
                                      <CloudinaryImage
                                        publicId={image.cloudinaryId}
                                        mode="original"
                                        className="h-32 w-full object-contain"
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex h-32 flex-col items-center justify-center rounded border border-dashed border-zinc-300 bg-zinc-50 text-center">
                                  <div className="text-sm text-zinc-500">
                                    No photos uploaded
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              </div>

              {event.categories && event.categories.length > 0 && (
                <div className="flex flex-col space-y-8">
                  <div className="mb-2">
                    <Heading level="h3">Categories</Heading>
                  </div>
                  <Card>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {event.categories.map((category) => (
                          <span
                            key={category.id}
                            className="rounded-full bg-zinc-100 px-3 py-1 text-sm"
                          >
                            {category.category?.name || 'Unknown'}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {event.tags && event.tags.length > 0 && (
                <div className="flex flex-col space-y-8">
                  <div className="mb-2">
                    <Heading level="h3">Tags</Heading>
                  </div>
                  <Card>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {event.tags.map((tag) => (
                          <span
                            key={tag.id}
                            className="rounded-full bg-zinc-100 px-3 py-1 text-sm"
                          >
                            {tag.tag?.name || 'Unknown'}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {event.organizers && event.organizers.length > 0 && (
                <div className="flex flex-col space-y-8">
                  <div className="mb-2">
                    <Heading level="h3">Organizers</Heading>
                  </div>
                  <Card>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-25">Name</TableHead>
                            <TableHead className="w-25">Email</TableHead>
                            <TableHead>Phone</TableHead>
                            <TableHead>Website</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {event.organizers.map((organizer) => (
                            <TableRow key={organizer.id}>
                              <TableCell>
                                {organizer.organizer?.name || '-'}
                              </TableCell>
                              <TableCell>
                                {organizer.organizer?.email || '-'}
                              </TableCell>
                              <TableCell>
                                {organizer.organizer?.phone || '-'}
                              </TableCell>
                              <TableCell>
                                {organizer.organizer?.website ? (
                                  <a
                                    href={organizer.organizer.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    {organizer.organizer.website}
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
            <div className="flex flex-col space-y-8 xl:col-span-1">
              {/* Preview Pane */}
              <div className="sticky top-4">
                <div className="mb-6 text-center">
                  <Heading level="h3">Preview</Heading>

                  <div className="mx-auto mt-2 flex max-w-xs items-stretch gap-1 rounded-lg bg-zinc-100 p-1">
                    <Button
                      variant={
                        previewMode === 'members-app' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'members-app' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('members-app')}
                      // aria-pressed={sortKey === 'firstName'}
                      size="sm"
                    >
                      Members App
                    </Button>
                    <Button
                      variant={
                        previewMode === 'events-site' ? 'secondary' : 'ghost'
                      }
                      className={cn(
                        'flex-1 hover:bg-white',
                        previewMode === 'events-site' && 'bg-white shadow-md',
                      )}
                      onClick={() => setPreviewMode('events-site')}
                      // aria-pressed={sortKey === 'lastName'}
                      size="sm"
                    >
                      Events Site
                    </Button>
                  </div>
                </div>
                {/* Phone Frame */}
                <PhonePreviewContainer>
                  {previewMode === 'members-app' ? (
                    <EventPreview event={event} venue={venue} />
                  ) : (
                    <EventsSitePreview event={event} venue={venue} />
                  )}
                </PhonePreviewContainer>
                {/* Preview in new tab button */}
                {previewMode === 'events-site' && (
                  <div className="z-10 mt-8 mb-2 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        useEventPreviewStore
                          .getState()
                          .setPreviewData({ event, venue });
                        window.open(
                          '/ticketed-events/preview',
                          'event-preview',
                        );
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Preview in new tab
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="ticket-types" className="mt-12">
          {event.ticketTypes && event.ticketTypes.length > 0 && (
            <div className="flex flex-col space-y-8">
              <div className="mb-2 flex items-center justify-between">
                <Heading level="h3">Ticket Types</Heading>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTicketTypesSheetOpen(true)}
                  >
                    Edit Ticket Types
                  </Button>
                </div>
              </div>
              <Card>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-25">Name</TableHead>
                        <TableHead className="w-25">Description</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Available</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.ticketTypes.map((ticketType) => (
                        <TableRow key={ticketType.id}>
                          <TableCell>{ticketType.name}</TableCell>
                          <TableCell className="max-w-75 truncate overflow-hidden text-ellipsis whitespace-nowrap">
                            {ticketType.description || '-'}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(ticketType.price)}
                          </TableCell>
                          <TableCell>{ticketType.available}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent value="ticket-orders" className="mt-12">
          <TicketOrders eventId={eventId || ''} />
        </TabsContent>
      </Tabs>

      <ArchiveEventDialog
        event={event}
        isOpen={eventArchiveSheetOpen}
        setIsOpen={setEventArchiveSheetOpen}
        userId={user?.id}
      />

      <RestoreEventDialog
        event={event}
        isOpen={eventRestoreDialogOpen}
        setIsOpen={setEventRestoreDialogOpen}
      />

      <EditTicketedEventDetailsSheet
        event={event}
        isOpen={eventDetailsSheetOpen}
        setIsOpen={setEventDetailsSheetOpen}
      />
      <EditTicketTypesSheet
        event={event}
        isOpen={ticketTypesSheetOpen}
        setIsOpen={setTicketTypesSheetOpen}
      />

      <FeaturedImageDialog
        name={event.name}
        resourceType="event"
        currentFeaturedAssetId={event.featuredImageId}
        isOpen={featuredImageDialogOpen}
        onOpenChange={setFeaturedImageDialogOpen}
        onSave={async (assetId) => {
          await updateEventMutation.mutateAsync({
            eventId: event.id,
            payload: { featuredImageId: assetId },
          });
          toastSuccess('Featured image updated successfully.');
        }}
      />

      <ManageGalleryImagesDialog
        name={event.name}
        currentAssetIds={event.images?.map((img) => img.id) ?? []}
        isOpen={galleryDialogOpen}
        onOpenChange={setGalleryDialogOpen}
        onSave={async (assetIds) => {
          await updateEventMutation.mutateAsync({
            eventId: event.id,
            payload: { imageIds: assetIds },
          });
          toastSuccess('Gallery images updated successfully.');
        }}
      />
    </div>
  );
}
